import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Logo } from "@/components/Nav";
import FlipBook from "@/components/FlipBook";
import PageView, { CoverView } from "@/components/PageView";
import { api, ratioOf } from "@/lib/api";

export default function SharedBook() {
  const { token } = useParams();
  const [book, setBook] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/shared/${token}`).then((r) => setBook(r.data)).catch(() => setErr(true));
  }, [token]);

  useEffect(() => {
    if (book) document.title = `${book.title} · Click, Print & Doodle`;
  }, [book]);

  const shell = (children) => (
    <div className="grain min-h-screen dark-mesh text-[#FAF8F5]">
      <header className="mx-auto flex max-w-7xl items-center justify-between px-5 py-5 sm:px-8">
        <div className="[&_*]:text-[#FAF8F5]"><Logo /></div>
        <Link to="/register" data-testid="shared-make-own-btn" className="btn-primary !px-4 !py-2">Make your own <ArrowRight className="h-4 w-4" /></Link>
      </header>
      {children}
    </div>
  );

  if (err) return shell(
    <main className="mx-auto max-w-xl px-5 py-32 text-center" data-testid="shared-not-found">
      <p className="overline !text-[#9DB8FF]">Link not active</p>
      <h1 className="mt-3 font-serif text-4xl">This book isn't shared any more.</h1>
      <p className="mt-4 text-sm text-[#FAF8F5]/60">The owner may have turned the link off. Ask them for a fresh one — or start a book of your own.</p>
    </main>
  );
  if (!book) return shell(<p className="py-32 text-center font-serif text-2xl text-[#FAF8F5]/60">Opening the album…</p>);

  const photos = Object.fromEntries(book.photos.map((p) => [p.id, p]));
  const props = { photos, size: book.size, theme: book.theme, enhance: book.enhance };
  const inner = book.pages.map((p, i) => <PageView key={p.id} page={p} {...props} testid={`shared-page-${i}`} />);
  if (inner.length % 2) inner.push(<div key="pad" className={`theme-${book.theme} absolute inset-0`} />);
  const pages = [
    <CoverView key="cover" book={book} photos={photos} enhance={book.enhance} testid="shared-cover" />,
    ...inner,
    <div key="back" className="absolute inset-0 flex flex-col items-center justify-center dark-mesh text-center text-[#FAF8F5]/70">
      <p className="font-serif text-xl text-[#FAF8F5] sm:text-3xl">Click, Print & Doodle</p>
      <p className="overline mt-2 !text-[#FAF8F5]/50">Printed in India · 300 DPI</p>
    </div>,
  ];

  return shell(
    <main className="mx-auto max-w-7xl px-5 pb-24 pt-6 sm:px-8" data-testid="shared-book-page">
      <div className="text-center">
        <p className="overline !text-[#9DB8FF]">Shared photo book · {book.pages.length} pages</p>
        <h1 className="mt-3 font-serif text-4xl tracking-tight sm:text-5xl" data-testid="shared-book-title">{book.title}</h1>
        {book.subtitle && <p className="mt-2 font-serif italic text-lg text-[#FAF8F5]/70">{book.subtitle}</p>}
        <p className="mt-4 text-xs text-[#FAF8F5]/50">Tap a page, swipe, or use the arrows to flip.</p>
      </div>
      <div className="mt-14 px-2 sm:px-6">
        <FlipBook pages={pages} id="shared" autoOpen ratio={ratioOf(book.size)} />
      </div>
      <div className="mx-auto mt-24 max-w-xl text-center">
        <h2 className="font-serif text-3xl tracking-tight">Have photos of your own piling up?</h2>
        <p className="mt-3 text-sm text-[#FAF8F5]/60">Upload them, and Click, Print & Doodle curates, captions and lays out a print-ready book — delivered to your door.</p>
        <Link to="/register" data-testid="shared-footer-cta" className="btn-primary mt-7">Start your book <ArrowRight className="h-4 w-4" /></Link>
      </div>
    </main>
  );
}
